import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import {
  Box, Paper, TextField, Button, Typography, Alert, CircularProgress, Stack, Divider,
} from '@mui/material'
import { useAuth } from '../../auth/AuthContext'
import { getConfig } from '../../config'

const schema = Yup.object({
  username: Yup.string().required('Ingresá tu usuario'),
  password: Yup.string().required('Ingresá tu contraseña'),
})

export default function LoginPage() {
  const { login } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [error, setError] = useState(null)
  const cfg = getConfig()
  const from = location.state?.from?.pathname || '/'

  const formik = useFormik({
    initialValues: { username: '', password: '' },
    validationSchema: schema,
    onSubmit: async (values, { setSubmitting }) => {
      setError(null)
      try {
        await login(values.username.trim(), values.password)
        navigate(from, { replace: true })
      } catch (e) {
        const status = e?.response?.status
        setError(status === 401 ? 'Usuario o contraseña incorrectos.' : 'No se pudo iniciar sesión. Intentá nuevamente.')
      } finally {
        setSubmitting(false)
      }
    },
  })

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #212b55 0%, #161d3d 100%)',
        p: 2,
      }}
    >
      <Paper elevation={8} sx={{ p: 4, width: '100%', maxWidth: 400, borderRadius: 3 }}>
        <Stack alignItems="center" spacing={0.5} mb={2}>
          <Box component="img" src="/brand/dpy-logo.png" alt="DPY" sx={{ height: 72, mb: 1 }} />
          <Typography variant="h6" fontWeight={600} color="primary">
            Facturación Ágil
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Iniciá sesión para emitir tus facturas
          </Typography>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <form onSubmit={formik.handleSubmit} noValidate>
          <TextField
            fullWidth margin="normal" label="Usuario" name="username"
            value={formik.values.username} onChange={formik.handleChange} onBlur={formik.handleBlur}
            error={formik.touched.username && !!formik.errors.username}
            helperText={formik.touched.username && formik.errors.username}
            autoFocus autoComplete="username"
          />
          <TextField
            fullWidth margin="normal" label="Contraseña" name="password" type="password"
            value={formik.values.password} onChange={formik.handleChange} onBlur={formik.handleBlur}
            error={formik.touched.password && !!formik.errors.password}
            helperText={formik.touched.password && formik.errors.password}
            autoComplete="current-password"
          />
          <Button
            type="submit" fullWidth variant="contained" color="secondary" size="large"
            disabled={formik.isSubmitting} sx={{ mt: 3, py: 1.2, fontWeight: 600 }}
          >
            {formik.isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Iniciar sesión'}
          </Button>
        </form>

        <Divider sx={{ my: 3 }} />
        <Stack alignItems="center" spacing={1}>
          <Box component="img" src="/brand/ekuatia.png" alt="e-kuatia" sx={{ height: 22, opacity: 0.55 }} />
          <Typography variant="caption" color="text.secondary" textAlign="center">
            © {cfg.brand}. Asunción, Paraguay · Powered by kiga
          </Typography>
        </Stack>
      </Paper>
    </Box>
  )
}
